import { useState, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Typography,
  Box,
  LinearProgress,
} from '@mui/material'
import { FileUpload } from '@mui/icons-material'
import { themesApi } from '@api/endpoints/themes'
import { CreateThemeRequest, ThemePaletteDto } from '@api/types/themes'
import { useMobileDialogProps } from '@components/common/useMobileDialogProps'

interface ThemeImportDialogProps {
  open: boolean
  onClose: () => void
  onComplete: () => void
}

const PALETTE_KEYS: (keyof ThemePaletteDto)[] = [
  'primary', 'primaryLight', 'primaryDark', 'primaryContrast',
  'secondary', 'secondaryLight', 'secondaryDark', 'secondaryContrast',
  'backgroundDefault', 'backgroundPaper', 'textPrimary', 'textSecondary',
]

function parsePalette(source: Record<string, unknown>): ThemePaletteDto {
  const palette: Record<string, string> = {}
  for (const key of PALETTE_KEYS) {
    palette[key] = typeof source[key] === 'string' ? (source[key] as string) : '#000000'
  }
  return palette as unknown as ThemePaletteDto
}

function parseTheme(data: Record<string, unknown>): CreateThemeRequest | null {
  if (!data.name || !data.slug) return null

  const lightSource = (data.light || data.lightPalette) as Record<string, unknown> | undefined
  const darkSource = (data.dark || data.darkPalette) as Record<string, unknown> | undefined

  if (!lightSource || !darkSource) return null

  return {
    name: data.name as string,
    slug: data.slug as string,
    description: (data.description as string) || '',
    previewPrimary: (data.previewPrimary as string) || '#000000',
    previewSecondary: (data.previewSecondary as string) || '#000000',
    isDefault: (data.isDefault as boolean) || false,
    sortOrder: (data.sortOrder as number) || 0,
    light: parsePalette(lightSource as Record<string, unknown>),
    dark: parsePalette(darkSource as Record<string, unknown>),
  }
}

export function ThemeImportDialog({ open, onClose, onComplete }: ThemeImportDialogProps) {
  const mobileDialogProps = useMobileDialogProps()
  const [jsonText, setJsonText] = useState('')
  const [importing, setImporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [progress, setProgress] = useState({ current: 0, total: 0 })
  const [results, setResults] = useState<{ success: number; failed: string[] } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileLoad = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      const text = e.target?.result
      if (typeof text === 'string') setJsonText(text)
    }
    reader.readAsText(file)
    event.target.value = ''
  }

  const handleImport = async () => {
    setError(null)
    setResults(null)

    let parsed: unknown
    try {
      parsed = JSON.parse(jsonText)
    } catch {
      setError('Invalid JSON format')
      return
    }

    const items = Array.isArray(parsed) ? parsed : [parsed]
    const themes: CreateThemeRequest[] = []

    for (let i = 0; i < items.length; i++) {
      const theme = parseTheme(items[i])
      if (!theme) {
        setError(`Item ${i + 1} is missing required fields (name, slug, light, dark)`)
        return
      }
      themes.push(theme)
    }

    if (themes.length === 0) {
      setError('No valid themes found in JSON')
      return
    }

    setImporting(true)
    setProgress({ current: 0, total: themes.length })
    const failed: string[] = []
    let success = 0

    for (let i = 0; i < themes.length; i++) {
      setProgress({ current: i + 1, total: themes.length })
      try {
        await themesApi.createTheme(themes[i])
        success++
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unknown error'
        failed.push(`${themes[i].name}: ${msg}`)
      }
    }

    setImporting(false)
    setResults({ success, failed })
    if (success > 0) onComplete()
  }

  const handleClose = () => {
    if (importing) return
    setJsonText('')
    setError(null)
    setResults(null)
    setProgress({ current: 0, total: 0 })
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth {...mobileDialogProps}>
      <DialogTitle>Import Themes</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {results && (
          <Alert severity={results.failed.length > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
            Imported {results.success} theme(s) successfully.
            {results.failed.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" fontWeight="bold">Failed:</Typography>
                {results.failed.map((msg, i) => (
                  <Typography key={i} variant="caption" display="block">{msg}</Typography>
                ))}
              </Box>
            )}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Paste a JSON array of themes or upload a JSON file. Each theme needs at minimum: name, slug, light palette, and dark palette.
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<FileUpload />}
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
          >
            Upload File
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            style={{ display: 'none' }}
            onChange={handleFileLoad}
          />
        </Box>

        <TextField
          multiline
          rows={10}
          fullWidth
          value={jsonText}
          onChange={(e) => setJsonText(e.target.value)}
          placeholder='[{ "slug": "my-theme", "name": "My Theme", ... }]'
          disabled={importing}
          sx={{ fontFamily: 'monospace' }}
        />

        {importing && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption">
              Importing {progress.current} / {progress.total}...
            </Typography>
            <LinearProgress
              variant="determinate"
              value={(progress.current / progress.total) * 100}
              sx={{ mt: 0.5 }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={importing}>Close</Button>
        <Button
          onClick={handleImport}
          variant="contained"
          disabled={importing || !jsonText.trim()}
        >
          {importing ? 'Importing...' : 'Import'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
